import React, { useState } from 'react';
import { useLanguage } from '../components/LanguageContext';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, MessageSquare, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export default function Contact() {
  const { language, isRTL } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setSubmitted(true);
    setLoading(false);
  };

  const contactInfo = [
    {
      icon: Mail,
      title: language === 'he' ? 'אימייל' : 'Email',
      value: 'support@aiphotobooth.com',
      color: 'violet'
    },
    {
      icon: Phone,
      title: language === 'he' ? 'טלפון' : 'Phone',
      value: '+972-50-000-0000',
      color: 'pink'
    },
    {
      icon: Clock,
      title: language === 'he' ? 'שעות פעילות' : 'Working Hours',
      value: language === 'he' ? 'א׳-ה׳ 9:00-18:00' : 'Sun-Thu 9:00-18:00',
      color: 'cyan'
    }
  ];

  const subjects = language === 'he' ? [
    { value: 'general', label: 'שאלה כללית' },
    { value: 'technical', label: 'תמיכה טכנית' },
    { value: 'billing', label: 'חיוב ותשלומים' },
    { value: 'partnership', label: 'שיתוף פעולה' },
    { value: 'feedback', label: 'משוב והצעות' }
  ] : [
    { value: 'general', label: 'General Question' },
    { value: 'technical', label: 'Technical Support' },
    { value: 'billing', label: 'Billing & Payments' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'feedback', label: 'Feedback & Suggestions' }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen py-16 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card rounded-2xl p-12 text-center max-w-md"
        >
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">
            {language === 'he' ? 'ההודעה נשלחה!' : 'Message Sent!'}
          </h2>
          <p className="text-slate-400 mb-6">
            {language === 'he' 
              ? 'תודה שפנית אלינו. נחזור אליך בהקדם האפשרי.'
              : 'Thank you for contacting us. We will get back to you as soon as possible.'}
          </p>
          <Button 
            onClick={() => setSubmitted(false)}
            variant="outline"
            className="border-slate-700 text-slate-300"
          >
            {language === 'he' ? 'שלח הודעה נוספת' : 'Send Another Message'}
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-white mb-4">
            {language === 'he' ? 'צור קשר' : 'Contact Us'}
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            {language === 'he' 
              ? 'יש לכם שאלות? נשמח לעזור! מלאו את הטופס ונחזור אליכם בהקדם.'
              : 'Have questions? We\'d love to help! Fill out the form and we\'ll get back to you soon.'}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <div className="space-y-4">
            {contactInfo.map((info, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card rounded-xl p-6"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl bg-${info.color}-600/20 flex items-center justify-center`}>
                    <info.icon className={`w-6 h-6 text-${info.color}-400`} />
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm">{info.title}</p>
                    <p className="text-white font-medium">{info.value}</p>
                  </div>
                </div>
              </motion.div>
            ))}

            {/* FAQ Hint */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-card rounded-xl p-6 bg-gradient-to-br from-violet-600/10 to-pink-600/10 border-violet-500/20"
            >
              <MessageSquare className="w-8 h-8 text-violet-400 mb-3" />
              <h3 className="text-white font-semibold mb-2">
                {language === 'he' ? 'שאלות נפוצות' : 'FAQ'}
              </h3>
              <p className="text-slate-400 text-sm">
                {language === 'he'
                  ? 'רוב התשובות נמצאות בעמוד המחירים שלנו. בדקו שם לפני שפונים אלינו.'
                  : 'Most answers can be found on our pricing page. Check there before contacting us.'}
              </p>
            </motion.div>
          </div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2"
          >
            <form onSubmit={handleSubmit} className="glass-card rounded-2xl p-8 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-300">
                    {language === 'he' ? 'שם מלא' : 'Full Name'}
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="bg-slate-800/50 border-slate-700 text-white h-12"
                    placeholder={language === 'he' ? 'השם שלך' : 'Your name'}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">
                    {language === 'he' ? 'אימייל' : 'Email'}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="bg-slate-800/50 border-slate-700 text-white h-12"
                    placeholder={language === 'he' ? 'your@email.com' : 'your@email.com'}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject" className="text-slate-300">
                  {language === 'he' ? 'נושא' : 'Subject'}
                </Label>
                <Select
                  value={formData.subject}
                  onValueChange={(value) => setFormData({ ...formData, subject: value })}
                >
                  <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white h-12">
                    <SelectValue placeholder={language === 'he' ? 'בחר נושא' : 'Select subject'} />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {subjects.map((subject) => (
                      <SelectItem key={subject.value} value={subject.value} className="text-slate-300">
                        {subject.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-slate-300">
                  {language === 'he' ? 'הודעה' : 'Message'}
                </Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={6}
                  className="bg-slate-800/50 border-slate-700 text-white resize-none"
                  placeholder={language === 'he' ? 'כתבו את ההודעה שלכם כאן...' : 'Write your message here...'}
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 text-white h-14 text-lg"
              >
                {loading ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full"
                  />
                ) : (
                  <>
                    <Send className="w-5 h-5 me-2" />
                    {language === 'he' ? 'שלח הודעה' : 'Send Message'}
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
